import {ChangeDetectionStrategy, Component, computed, ElementRef, Injectable, signal} from "@angular/core";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {OppgaverPopupKomponent} from "web-ng2/moduler/dashboard/komponenter/oppgaver-popup/oppgaver-popup";
import {AntallOppgaverPerBrukerModell, AvstemningMedMøteModell, AvstemningRessursClient, DokumenterTilSigneringModell, DokumentsigneringRessursClient, ManglerBesvarelsePåSpørreundersøkelseModell, ManglerStemmegivningModell, ModelloppdateringType, OppgaveModell, OppgaveRessursClient, SpørreundersøkelseRessursClient, TilgangsrolleMedlem} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {BrukerkontekstTilbyder} from "felles-ng2/tjenester/BrukerkontekstTilbyder";
import {takeUntilDestroyed, toSignal} from "@angular/core/rxjs-interop";
import {BehaviorSubject, combineLatest, debounceTime, filter, fromEvent, merge, Observable, of, skip, startWith, switchMap} from "rxjs";
import {TranslateModule} from "@ngx-translate/core";
import {DokumenterTilSigneringPopupKomponent} from "web-ng2/moduler/dashboard/komponenter/dokumenter-til-signering-popup/dokumenter-til-signering-popup";
import {AvstemningerPopupKomponent} from "web-ng2/moduler/dashboard/komponenter/avstemninger-popup/avstemninger-popup";
import {tomDokumenterTilSigneringModell} from "felles-ng2/modell/tommeModeller";
import {distinkte, erIkkeTomListe, reduserTilSum} from "felles-ng2/støttefunksjoner/lister";
import {dokumenterSomSkalSigneresAvAndreEnn} from "felles-ng2/modell/dokumentSignatarerModellHjelpere";
import {map} from "rxjs/operators";
import {begrensTilVerdiMellom} from "felles-ng2/støttefunksjoner/tall";
import {MatIcon} from "@angular/material/icon";
import {fadeInnUtAnimasjon} from "web-ng2/delt/animasjoner";
import {erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {escapeTastTrykket} from "felles-ng2/støttefunksjoner/tastaturhendelser";
import {OppgaveDatatjeneste} from "felles-ng2/tjenester/datalager/OppgaveDatatjeneste";
import {mapTilData} from "felles-ng2/tjenester/datalager/DataOgMeta";
import {maxZonedDateTime} from "felles-ng2/støttefunksjoner/temporal";
import {SpørreundersøkelserPopupKomponent} from "web-ng2/moduler/dashboard/komponenter/spørreundersøkelser-popup/spørreundersøkelser-popup";
import {Modelloppdateringer} from "felles-ng2/tjenester/Modelloppdateringer";

const debug = initDebugger('DashboardStatusKomponent');

/** Viser oversikt over ikke-utførte oppgaver, utestående signeringer, avstemninger det skulle vært stemt på og spørreundersøkelser som skulle vært besvart */
@Component({
    selector: 'dashboard-status',
    templateUrl: 'dashboard-status.html',
    styleUrl: 'dashboard-status.scss',
    host: {
        '(click)': '$event.stopPropagation()', //forhindrer at klikk i denne komponenten lukker popups
        'data-testid': 'dashboard-status'
    },
    animations: [fadeInnUtAnimasjon('200ms')],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [TranslateModule, OppgaverPopupKomponent, DokumenterTilSigneringPopupKomponent, AvstemningerPopupKomponent, MatIcon, SpørreundersøkelserPopupKomponent]
})
export class DashboardStatusKomponent {
    static ajsSelector = 'dashboardStatus';
    private static minPopupHøyde = 200;

    harSkrivetilgang = this.kontekst.autentisertBruker().inneharTilgangsrolleINoenGruppe(TilgangsrolleMedlem.skrive);

    /** Lytt til endring av nettleser-vindu-størrelse og beregn maks popup-høyde */
    maksPopupHoeyde = toSignal(fromEvent(window, 'resize').pipe(startWith(undefined), debounceTime(500), map(() =>
      this.beregnMaksPopuphøyde())), {initialValue: DashboardStatusKomponent.minPopupHøyde});

    private relastDokumenterTilSignering = new BehaviorSubject<void>(undefined);
    viserDokumenterTilSignering = signal<boolean>(false);
    /** Dokumenter som bruker ikke har signert, hentet fra server, brukes både i teller i statusvisning og i popup */
    dokumenterBrukerIkkeHarSignert = toSignal(this.relastDokumenterTilSignering.pipe(switchMap(() =>
      this.avhengigheter.dokumenterBrukerIkkeHarSignert())), {initialValue: tomDokumenterTilSigneringModell()});
    /** Dokumenter som øvrige brukere ikke har signert, hentet fra server, brukes både i teller i statusvisning og i popup */
    dokumenterAndreSkalSignere = toSignal(this.relastDokumenterTilSignering.pipe(switchMap(() =>
      this.lastDokumenterAndreSkalSignere())), {initialValue: tomDokumenterTilSigneringModell()});
    antallDokumenterTilSignering = computed(() => this.beregnAntallDokumenterTilSignering());

    private relastOppgaver = new BehaviorSubject<void>(undefined);
    /** Oppgaver som bruker ikke har utført, hentet fra server, brukes kun i popup, derfor dropper vi initiell lasting (vhja skip(1)) */
    viserOppgaver = signal<boolean>(false);
    brukersOppgaver = toSignal(this.relastOppgaver.pipe(skip(1), switchMap(() =>
      this.avhengigheter.brukersOppgaver())), {initialValue: []});
    /** Oppgaver som øvrige ikke har utført, hentet fra server, brukes både i teller i statusvisning og i popup */
    antallOppgaverPerBruker = toSignal(this.relastOppgaver.pipe(switchMap(() =>
      this.avhengigheter.antallOppgaverPerBruker())), {initialValue: []});
    antallOppgaver = computed(() => this.beregnAntallOppgaver(this.antallOppgaverPerBruker(), this.brukersOppgaver()));

    private relastAvstemninger = new BehaviorSubject<void>(undefined);
    viserAvstemninger = signal<boolean>(false);
    /** Avstemninger som bruker ikke har besvart, hentet fra server, brukes både i teller i statusvisning og i popup */
    avstemningerBrukerIkkeHarStemtPaa = toSignal(this.relastAvstemninger.pipe(switchMap(() =>
      this.avhengigheter.avstemningerBrukerIkkeHarStemtPaa())), {initialValue: []});
    /** Avstemninger som øvrige ikke har besvart, hentet fra server, brukes både i teller i statusvisning og i popup */
    avstemningerAndreIkkeHarStemtPaa = toSignal(this.relastAvstemninger.pipe(switchMap(() =>
      this.lastAvstemningerAndreIkkeHarStemtPaa())), {initialValue: []});
    antallAvstemninger = computed(() => this.beregnAntallAvstemninger(this.avstemningerBrukerIkkeHarStemtPaa(), this.avstemningerAndreIkkeHarStemtPaa()));

    private relastUndesøkelser = new BehaviorSubject<void>(undefined);
    viserUndersoekelser = signal<boolean>(false);
    /** Ubesvarte spørreundersøkelser, hentet fra server, brukes både i teller i statusvisning og i popup */
    ubesvarteUndersoekelser = toSignal(this.relastUndesøkelser.pipe(switchMap(() =>
      this.avhengigheter.ubesvarteSpørreundersøkelser())), {initialValue: []});
    antallUndersoekelser = computed(() => this.ubesvarteUndersoekelser().length);

    constructor(private avhengigheter: DashboardStatusKomponentAvhengigheter,
                private el: ElementRef<HTMLElement>,
                private kontekst: BrukerkontekstTilbyder,
                private modelloppdateringer: Modelloppdateringer,) {
        this.lukkPopupVedEscapeOgKlikkUtenfor();

        // Modelloppdateringer påvirker antall
        this.lyttTilModelloppdateringer();
    }

    aapneEllerLukkDokumenterTilSignering() {
        this.lukkOppgaver();
        this.lukkAvstemninger();
        this.lukkUndersoekelser();

        this.viserDokumenterTilSignering.update(viser => !viser);
        if (this.viserDokumenterTilSignering()) this.relastDokumenterTilSignering.next();
    }

    lukkDokumenterTilSignering() {
        this.viserDokumenterTilSignering.set(false);
    }

    aapneEllerLukkOppgaver() {
        this.lukkDokumenterTilSignering();
        this.lukkAvstemninger();
        this.lukkUndersoekelser();

        this.viserOppgaver.update(viser => !viser);
        if (this.viserOppgaver()) this.relastOppgaver.next();
    }

    lukkOppgaver() {
        this.viserOppgaver.set(false);
    }

    aapneEllerLukkAvstemninger() {
        this.lukkDokumenterTilSignering();
        this.lukkOppgaver();
        this.lukkUndersoekelser();

        this.viserAvstemninger.update(viser => !viser);
        if (this.viserAvstemninger()) this.relastAvstemninger.next();
    }

    lukkAvstemninger() {
        this.viserAvstemninger.set(false);
    }

    aapneEllerLukkUndersoekelser() {
        this.lukkDokumenterTilSignering();
        this.lukkAvstemninger();
        this.lukkOppgaver();

        this.viserUndersoekelser.update(viser => !viser);
        if (this.viserUndersoekelser()) this.relastUndesøkelser.next();
    }

    lukkUndersoekelser(relastData = false) {
        this.viserUndersoekelser.set(false);
        if (relastData) this.relastUndesøkelser.next();
    }

    // Hjelpemetoder

    /**
     * Beregner maksimal høyde for popup.
     *
     * ```
     * ────── del av basissideInnholdElement skrollet utenfor topp av vindu ────────────
     *   ↑
     *   ├ basissideInnholdElement?.scrollTop (> 0 dersom basissideInnholdElement er scrollet opp, dvs at toppen av basissideInnholdElement forsvinner utenfor toppen av window)
     *   ↓
     * ────── window ───────────────────────────────────────────────────────────────────┐
     * ↑ ↑    <her vises søkefelt og eventuell meldinger med gull bakgrunn>             │
     * │ │ ────── basissideInnholdElement (#basissideInnhold - scrollbart område) ──┐   │
     * │ │    <her vises eventuelt innhold mellom søkefelt og status tittelrad>     │   │
     * │ │  ─────── statuserElement (div.statuser) ─────────────────────────────┐   │   │
     * │ │    <her vises statusene>                                             │   │   │
     * │ ├ statuserBunnVertDist (statuserElement.getBoundingClientRect().bottom)│   │   │
     * │ ↓                                                                      │   │   │
     * │    ────────────────────────────────────────────────────────────────────┘   │   │
     * │    ─────── popup (div.popup-kontainer)  ───────────────────────────────┐   │   │
     * │    ↑                                                                   │   │   │
     * │    │ <her vises popup-innholdet når bruke har klikket på en status>    │   │   │
     * │    ├ maksPopuphøyde sørger for at popup ikke blir høyere enn           │   │   │
     * │    │ tilgjengelig plass til bunn av window                             │   │   │
     * │    ↓                                                                   │   │   │
     * │    ────────────────────────────────────────────────────────────────────┘   │   │
     * │  ──────────────────────────────────────────────────────────────────────────┘   │
     * │                                                                                │
     * ├ window.innerHeight                                                             │
     * ↓                                                                                │
     * ─────────────────────────────────────────────────────────────────────────────────┘
     * ```
     */
    private beregnMaksPopuphøyde() {
        const basissideInnholdElement = document.querySelector('#basissideInnhold') as HTMLElement | null; //normalt ikke null, men ved kjøring i storybook finnes ikke nødvendigvis dette elementet
        const statuserElement = this.el.nativeElement;
        //tar hensyn til at bruker kan ha scrollet innholdet opp, slik at popup ikke forsvinner ut av synlig område når bruker scroller innholdet ned igjen
        const scrollTop = basissideInnholdElement?.scrollTop ?? 0;
        const statuserBunnVertDist = Math.floor(statuserElement.getBoundingClientRect().bottom + scrollTop);
        let høyde = window.innerHeight - statuserBunnVertDist - 50; //-50 slik at popupen nedre kant ligger ca 50px over nettleservinduet nedre kant
        return begrensTilVerdiMellom(høyde, DashboardStatusKomponent.minPopupHøyde, 900);
    }

    /** Lukker popup ved klikk på escape og ved klikk utenfor popup */
    private lukkPopupVedEscapeOgKlikkUtenfor() {
        merge(
          escapeTastTrykket(),
          //ved klikke - men ikke dersom klikket opprinner fra mat overlay (feks ved klikk i mat-meny i OppgaveListevisningKompaktKomponent)
          //merk at klikk i popup allerede er håndtert vhja `host: {'(click)': '$event.stopPropagation()`
          fromEvent(document, 'click').pipe(filter(event => erNil((event.target as HTMLElement).closest('.cdk-overlay-container')))))
          .pipe(takeUntilDestroyed())
          .subscribe(() => {
              this.lukkDokumenterTilSignering();
              this.lukkOppgaver();
              this.lukkAvstemninger();
          });
    }

    private lastDokumenterAndreSkalSignere(): Observable<DokumenterTilSigneringModell> {
        if (!this.harSkrivetilgang) return of(tomDokumenterTilSigneringModell());
        return this.avhengigheter.dokumenterSomManglerSignaturer().pipe(
          map(dokumenter => this.kunDokumenterAndreSkalSignere(dokumenter)));
    }

    private lastAvstemningerAndreIkkeHarStemtPaa(): Observable<ManglerStemmegivningModell[]> {
        if (!this.harSkrivetilgang) return of([]);
        return this.avhengigheter.avstemningerAndreIkkeHarStemtPaa();
    }

    private beregnAntallDokumenterTilSignering(): number {
        return distinkte([this.dokumenterBrukerIkkeHarSignert(), this.dokumenterAndreSkalSignere()]
          .flatMap(dokumenter => [...dokumenter.arkivdokumenter, ...dokumenter.saksdokumenter, ...dokumenter.moeter])
          .map(tilSignering => tilSignering.tilSignering.id))
          .length
    }

    private kunDokumenterAndreSkalSignere(dokumenterTilSignering: DokumenterTilSigneringModell): DokumenterTilSigneringModell {
        return {
            arkivdokumenter: dokumenterSomSkalSigneresAvAndreEnn(dokumenterTilSignering.arkivdokumenter, this.kontekst.autentisertBruker().bruker),
            saksdokumenter: dokumenterSomSkalSigneresAvAndreEnn(dokumenterTilSignering.saksdokumenter, this.kontekst.autentisertBruker().bruker),
            moeter: dokumenterSomSkalSigneresAvAndreEnn(dokumenterTilSignering.moeter, this.kontekst.autentisertBruker().bruker)
        };
    }

    private beregnAntallOppgaver(antallOppgaverPerBruker: AntallOppgaverPerBrukerModell[], brukersOppgaver: OppgaveModell[]): number {
        //antallForfalte på tvers av brukere, såfremt innlogget bruker har skriverettigheter
        const antallOppgaverAndreBrukere = this.harSkrivetilgang ?
          antallOppgaverPerBruker
            .filter(bruker => bruker.bruker?.id !== this.kontekst.autentisertBruker().bruker.id)
            .map(bruker => bruker.antallForfalte)
            .reduce(...reduserTilSum()) :
          0;

        //dersom brukersOppgaver er lastet fra server bruker vi denne til å beregne - da vil også tellingen bli oppdatert dersom bruker markerer oppgave som utført direkte i oppgaver-popup
        const antallOppgaverInnloggetBruker = erIkkeTomListe(brukersOppgaver) ?
          brukersOppgaver.filter(oppgave => !oppgave.utfoert).length :
          antallOppgaverPerBruker
            .filter(bruker => bruker.bruker?.id === this.kontekst.autentisertBruker().bruker.id)
            .map(bruker => bruker.antallIkkeUtfoerte)
            .reduce(...reduserTilSum());

        return antallOppgaverInnloggetBruker + antallOppgaverAndreBrukere;
    }

    private beregnAntallAvstemninger(brukerIkkeStemt: AvstemningMedMøteModell[], andreIkkeStemt: ManglerStemmegivningModell[]): number {
        //innlogget brukers manglende stemmegivning
        return brukerIkkeStemt.length
          //manglende stemmegivning på tvers av brukere, dersom innlogget bruker har skriverettigheter
          + andreIkkeStemt
            .filter(() => this.harSkrivetilgang) //kun dersom bruker har skriverettigheter
            .map(manglerStemme => manglerStemme.antallAvstemninger -
              //-1  dersom allerede telt via brukerIkkeStemt/
              (brukerIkkeStemt.some(b => b.moeteId === manglerStemme.moete.id) ? 1 : 0))
            .reduce(...reduserTilSum());
    }

    private lyttTilModelloppdateringer() {
        //dropper å lytte til endringer i oppgaver da oppdatering av innlogget brukers oppgaver er håndtert indirekte via OppgaveDatatjeneste.finnIkkeUtførteOppgaverMedForfallFør
        //vi får dog ikke med oss endringer i andre brukeres oppgaver
        this.modelloppdateringer.hendelser.pipe(
          filter(hendelse => [ModelloppdateringType.NY_OPPGAVE, ModelloppdateringType.OPPGAVE_SLETTET].includes(hendelse.type)))
          .subscribe(() => this.relastOppgaver.next());

        this.modelloppdateringer.hendelser.pipe(
          filter(hendelse => ModelloppdateringType.DOKUMENT_KOMPLETTSIGNERT == hendelse.type))
          .subscribe(() => this.relastDokumenterTilSignering.next());
    }
}

//@todo feilhåndtering
@Injectable()
export class DashboardStatusKomponentAvhengigheter {
    constructor(private oppgaveDatatjeneste: OppgaveDatatjeneste,
                private oppgaveRessurs: OppgaveRessursClient,
                private avstemningRessurs: AvstemningRessursClient,
                private dokumentsigneringRessurs: DokumentsigneringRessursClient,
                private spørreundersøkelseRessurs: SpørreundersøkelseRessursClient) {
    }

    antallOppgaverPerBruker(): Observable<AntallOppgaverPerBrukerModell[]> {
        return this.oppgaveRessurs.antallOppgaverPerBruker().somObservable();
    }

    brukersOppgaver(): Observable<OppgaveModell[]> {
        return combineLatest({
            ikkeUtførte: this.oppgaveDatatjeneste.finnIkkeUtførteOppgaverMedForfallFør(maxZonedDateTime).pipe(mapTilData()),
            utenFrist: this.oppgaveDatatjeneste.finnIkkeUtfoerteOppgaverUtenFrist().pipe(mapTilData())
        })
          .pipe(map(oppgaver => [...oppgaver.ikkeUtførte.reverse(), ...oppgaver.utenFrist]))
    }

    avstemningerBrukerIkkeHarStemtPaa(): Observable<AvstemningMedMøteModell[]> {
        return this.avstemningRessurs.finnAvstemningerBrukerIkkeHarStemtPå().somObservable();
    }

    avstemningerAndreIkkeHarStemtPaa(): Observable<ManglerStemmegivningModell[]> {
        return this.avstemningRessurs.finnAvstemningerAndreIkkeHarStemtPå().somObservable();
    }

    dokumenterBrukerIkkeHarSignert(): Observable<DokumenterTilSigneringModell> {
        return this.dokumentsigneringRessurs.finnDokumenterBrukerIkkeHarSignert().somObservable();
    }

    dokumenterSomManglerSignaturer(): Observable<DokumenterTilSigneringModell> {
        return this.dokumentsigneringRessurs.finnDokumenterSomManglerSignaturer().somObservable();
    }

    ubesvarteSpørreundersøkelser(): Observable<ManglerBesvarelsePåSpørreundersøkelseModell[]> {
        return this.spørreundersøkelseRessurs.finnUbesvarte().somObservable();
    }
}
